import _ from 'lodash'
import React from 'react'

import {
  applyTagTransform,
  getEntityOwnerFirmId,
  getOptionSource,
  getReferenceListKey,
  resolveEntityTypeId,
  useDistinctFieldValues,
  useReferenceListOptions,
} from 'browser/components/atomic-elements/molecules/fields/reference-list-options'

/**
 * HOC that resolves a select field's options from its `optionSource` (and
 * normalizes creatable values via a `transform` formula). This is what lets a
 * plain `ui:selectField` do everything the retired `ui:tagsField` did — a
 * creatable multi-select with firm-managed and/or self-suggesting options —
 * without a bespoke component.
 *
 * Options precedence: firm reference list → distinct existing values
 * (`optionSource.distinctValues`) → the field's static enum/suggestions. The
 * reference list is scoped to the entity's owner firm, so a cross-firm switcher
 * sees the owner firm's vocabulary.
 *
 * No-ops (renders the wrapped component untouched) when the field declares
 * neither an optionSource nor a transform, so it is safe on the shared select.
 */
export function withOptionSource<
  P extends {
    entity?: any
    entityType?: string
    frames?: any
    options?: any[]
    isLoading?: boolean
    onChange?: (values: any, selectedOptions?: any) => void
    transform?: string
    suggestionsSize?: number
  }
>(Wrapped: React.ComponentType<P>): React.FC<P> {
  const WithOptionSource: React.FC<P> = (props) => {
    const optionSource = getOptionSource(props)
    const referenceListKey = getReferenceListKey(props)
    const wantsDistinct = !!optionSource?.distinctValues
    const { transform } = props

    const ownerFirmId = getEntityOwnerFirmId(props.entity)
    const valuePath: string[] = props.frames?.getContext?.('valuePath') ?? []

    const { options: referenceOptions, isLoading: isLoadingReference, loaded: referenceLoaded } =
      useReferenceListOptions(referenceListKey, ownerFirmId)

    // Only fetch distinct values when enabled AND the reference list produced
    // nothing (it takes precedence). Passing undefined entityType skips the load.
    const distinctEntityType =
      wantsDistinct && referenceLoaded && _.isEmpty(referenceOptions)
        ? resolveEntityTypeId(props.entity, props.entityType, valuePath)
        : undefined
    const { options: distinctOptions, isLoading: isLoadingDistinct } = useDistinctFieldValues(
      distinctEntityType,
      valuePath,
      props.suggestionsSize
    )

    if (!referenceListKey && !wantsDistinct && !transform) {
      return <Wrapped {...props} />
    }

    // Strict precedence, not a union: a firm reference list fully replaces the
    // static suggestions (showing both is confusing — see the trailer type
    // dropdown). Only when no reference list resolves do we fall back to the
    // distinct existing values + the field's static enum/suggestions.
    const options = _.isEmpty(referenceOptions)
      ? _.uniqBy([...distinctOptions, ...(props.options ?? [])], 'value')
      : _.uniqBy(referenceOptions, 'value')
    const onChange = transform
      ? (values: any, selectedOptions?: any) => props.onChange?.(applyTagTransform(transform, values), selectedOptions)
      : props.onChange

    return (
      <Wrapped
        {...props}
        options={options}
        onChange={onChange}
        isLoading={props.isLoading || isLoadingReference || isLoadingDistinct}
      />
    )
  }
  WithOptionSource.displayName = `withOptionSource(${Wrapped.displayName || Wrapped.name || 'Component'})`
  return WithOptionSource
}
